import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isCalFlow } from '../utils/calFlow';
import CalOnboarding from './CalOnboarding';
import {
  ChartBarIcon,
  FolderIcon,
  SparklesIcon,
  CheckIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

// ─── Slideshow data ────────────────────────────────────────────────────────────
const slides = [
  {
    icon: FolderIcon,
    accent: '#305CFE',
    title: 'Signal-Driven Model Portfolios',
    body: 'Browse 15+ institutional model portfolios ranked by our proprietary Power List™. Each model is scored using alternative data signals — giving you an edge before the crowd catches on.',
    tag: 'Model Intelligence',
  },
  {
    icon: ChartBarIcon,
    accent: '#00D4FF',
    title: 'Quantitative Research at Your Fingertips',
    body: 'Analyze ETFs and tickers with institutional-grade factor data, sector exposures, and historical signal overlays. Make allocation decisions backed by real quant research.',
    tag: 'Deep Analytics',
  },
  {
    icon: SparklesIcon,
    accent: '#10B981',
    title: 'AI Research Assistant',
    body: 'Ask anything about markets, models, or positions. Our AI is trained on your platform data so it can answer questions no generic chatbot can — and it never hallucinates portfolio data.',
    tag: 'AI-Powered',
  },
];

// ─── Plan options ──────────────────────────────────────────────────────────────
const plans = [
  {
    id: 'trial',
    name: 'Free 30-Day Trial',
    price: 'No charge',
    period: null,
    description: 'Full access, no credit card required.',
    badge: 'Recommended',
    badgeColor: 'bg-signal-positive text-white',
    highlight: true,
    cta: 'Start free trial',
  },
  {
    id: 'quarterly',
    name: 'Quarterly Plan',
    price: '$1,500',
    period: 'every 3 months',
    description: 'Recurring quarterly billing.',
    badge: null,
    highlight: false,
    cta: 'Get quarterly access',
  },
  {
    id: 'annual',
    name: 'Annual Plan',
    price: '$4,499',
    period: 'per year',
    description: 'Save 25% vs quarterly — best value.',
    badge: 'Save 25%',
    badgeColor: 'bg-primary-100 text-white',
    highlight: false,
    cta: 'Get annual access',
  },
];

// ─── Profile questions ─────────────────────────────────────────────────────────
const roles = [
  'Registered Investment Advisor (RIA)',
  'Broker-Dealer / Wirehouse',
  'Family Office',
  'Institutional Investor',
  'Portfolio Manager',
  'Financial Planner / CFP',
  'Other',
];

const aumRanges = [
  'Under $10M',
  '$10M – $50M',
  '$50M – $100M',
  '$100M – $500M',
  '$500M – $1B',
  'Over $1B',
];

const referralSources = [
  'Google / Search',
  'LinkedIn',
  'Colleague / Referral',
  'Conference / Event',
  'Industry Publication',
  'Other',
];

// ─── Step indicator ────────────────────────────────────────────────────────────
function StepDots({ total, current }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`rounded-full transition-all duration-300 ${
            i === current
              ? 'w-6 h-2 bg-primary-100'
              : i < current
              ? 'w-2 h-2 bg-primary-100 opacity-50'
              : 'w-2 h-2 bg-bg-300'
          }`}
        />
      ))}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function OnboardingPage() {
  const { user, userRecord, activateTrial, selectPaidPlan, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const calFlow = isCalFlow(searchParams.toString());

  // Already onboarded — skip straight to the app
  useEffect(() => {
    if (userRecord?.onboarding_completed === true) {
      navigate('/models', { replace: true });
    }
  }, [userRecord, navigate]);

  // Returning from Stripe checkout — jump straight to the slideshow
  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      setStep(3);
    }
  }, [searchParams]);

  // Steps: 0=info1, 1=info2, 2=subscription, 3=slideshow
  const [step, setStep] = useState(0);
  const TOTAL_STEPS = 3; // steps before slideshow

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    firm_name: '',
    phone: '',
    role: '',
    aum_range: '',
    referral_source: '',
  });

  const [selectedPlan, setSelectedPlan] = useState('trial');
  const [slideIndex, setSlideIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // ── Step 0: basic info ──────────────────────────────────────────────────────
  const step0Valid = form.first_name.trim() && form.last_name.trim() && form.firm_name.trim();

  // ── Step 1: professional details ───────────────────────────────────────────
  const step1Valid = form.role && form.aum_range && form.referral_source;

  // ── Handle subscription selection ──────────────────────────────────────────
  const handleSubscribe = async () => {
    setLoading(true);
    setError('');
    try {
      const profileData = {
        data: {
          first_name: form.first_name,
          last_name: form.last_name,
          firm_name: form.firm_name,
          phone: form.phone,
          role: form.role,
          aum_range: form.aum_range,
          referral_source: form.referral_source,
        },
      };

      if (selectedPlan === 'trial') {
        await activateTrial(profileData.data);
        setStep(3);
      } else {
        // Paid plan — marks pending_payment, creates Stripe checkout session
        const response = await selectPaidPlan(selectedPlan, profileData.data);
        const checkout_url = response?.data?.checkout_url;
        if (checkout_url) {
          window.location.href = checkout_url;
          return; // navigation away — don't advance the step
        }
        setStep(3);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Finish slideshow — this is the final onboarding step ───────────────────
  const handleFinish = async () => {
    setLoading(true);
    try {
      await completeOnboarding(); // sets onboarding_completed = true
    } catch (_) {}
    navigate('/models', { replace: true });
  };

  if (calFlow) {
    return <CalOnboarding />;
  }

  // ── Render steps ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-bg-100 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-bg-300">
          <img src="/logo_wordmark.svg" alt="SharpeMetrix" className="h-7" />
        {step < 3 && (
          <div className="flex items-center gap-4">
            <span className="text-text-300 text-sm">Step {step + 1} of {TOTAL_STEPS}</span>
            <StepDots total={TOTAL_STEPS} current={step} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">

          {/* ── Step 0: Personal info ── */}
          {step === 0 && (
            <div className="space-y-8 animate-fade-in">
              <div>
                <p className="text-primary-100 text-sm font-medium mb-1">Welcome, {user?.email}</p>
                <h1 className="text-3xl font-bold text-text-100">Tell us about yourself</h1>
                <p className="mt-2 text-text-200">We'll personalise your experience based on your profile.</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-200 mb-1.5">First name</label>
                    <input
                      type="text"
                      value={form.first_name}
                      onChange={(e) => setField('first_name', e.target.value)}
                      placeholder="Jane"
                      className="block w-full rounded-lg bg-bg-200 px-4 py-3 text-text-100 ring-1 ring-bg-300 placeholder:text-text-300 focus:ring-2 focus:ring-primary-100 text-sm transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-200 mb-1.5">Last name</label>
                    <input
                      type="text"
                      value={form.last_name}
                      onChange={(e) => setField('last_name', e.target.value)}
                      placeholder="Smith"
                      className="block w-full rounded-lg bg-bg-200 px-4 py-3 text-text-100 ring-1 ring-bg-300 placeholder:text-text-300 focus:ring-2 focus:ring-primary-100 text-sm transition-all outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-200 mb-1.5">Firm / Company name</label>
                  <input
                    type="text"
                    value={form.firm_name}
                    onChange={(e) => setField('firm_name', e.target.value)}
                    placeholder="Acme Capital Management"
                    className="block w-full rounded-lg bg-bg-200 px-4 py-3 text-text-100 ring-1 ring-bg-300 placeholder:text-text-300 focus:ring-2 focus:ring-primary-100 text-sm transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-200 mb-1.5">Phone number</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="block w-full rounded-lg bg-bg-200 px-4 py-3 text-text-100 ring-1 ring-bg-300 placeholder:text-text-300 focus:ring-2 focus:ring-primary-100 text-sm transition-all outline-none"
                  />
                </div>
              </div>

              <button
                onClick={() => setStep(1)}
                disabled={!step0Valid}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-200 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ── Step 1: Professional details ── */}
          {step === 1 && (
            <div className="space-y-8">
              <div>
                <h1 className="text-3xl font-bold text-text-100">Your practice</h1>
                <p className="mt-2 text-text-200">Help us tailor the platform to your needs.</p>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-text-200 mb-2">I am a...</label>
                  <div className="grid grid-cols-1 gap-2">
                    {roles.map((r) => (
                      <button
                        key={r}
                        onClick={() => setField('role', r)}
                        className={`text-left px-4 py-3 rounded-lg text-sm transition-all border ${
                          form.role === r
                            ? 'bg-primary-100 bg-opacity-15 border-primary-100 text-text-100'
                            : 'border-bg-300 text-text-200 hover:border-bg-300 hover:bg-bg-200'
                        }`}
                      >
                        <span className="flex items-center justify-between">
                          {r}
                          {form.role === r && <CheckIcon className="w-4 h-4 text-primary-100 shrink-0" />}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-200 mb-2">Assets under management</label>
                  <div className="grid grid-cols-2 gap-2">
                    {aumRanges.map((r) => (
                      <button
                        key={r}
                        onClick={() => setField('aum_range', r)}
                        className={`text-left px-4 py-3 rounded-lg text-sm transition-all border ${
                          form.aum_range === r
                            ? 'bg-primary-100 bg-opacity-15 border-primary-100 text-text-100'
                            : 'border-bg-300 text-text-200 hover:bg-bg-200'
                        }`}
                      >
                        <span className="flex items-center justify-between">
                          {r}
                          {form.aum_range === r && <CheckIcon className="w-4 h-4 text-primary-100 shrink-0" />}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-200 mb-2">How did you hear about us?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {referralSources.map((s) => (
                      <button
                        key={s}
                        onClick={() => setField('referral_source', s)}
                        className={`text-left px-4 py-3 rounded-lg text-sm transition-all border ${
                          form.referral_source === s
                            ? 'bg-primary-100 bg-opacity-15 border-primary-100 text-text-100'
                            : 'border-bg-300 text-text-200 hover:bg-bg-200'
                        }`}
                      >
                        <span className="flex items-center justify-between">
                          {s}
                          {form.referral_source === s && <CheckIcon className="w-4 h-4 text-primary-100 shrink-0" />}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(0)}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-text-200 hover:text-text-100 border border-bg-300 hover:border-bg-300 transition-all"
                >
                  <ArrowLeftIcon className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!step1Valid}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-200 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Subscription ── */}
          {step === 2 && (
            <div className="space-y-8">
              <div>
                <h1 className="text-3xl font-bold text-text-100">Choose your plan</h1>
                <p className="mt-2 text-text-200">
                  Start free — upgrade any time. No credit card needed for the trial.
                </p>
              </div>

              <div className="space-y-3">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full text-left rounded-xl p-5 border-2 transition-all ${
                      selectedPlan === plan.id
                        ? 'border-primary-100 bg-primary-100 bg-opacity-10'
                        : 'border-bg-300 bg-bg-200 hover:border-bg-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-text-100">{plan.name}</span>
                          {plan.badge && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${plan.badgeColor}`}>
                              {plan.badge}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-baseline gap-1">
                          <span className={`text-2xl font-bold ${plan.highlight ? 'text-signal-positive' : 'text-text-100'}`}>
                            {plan.price}
                          </span>
                          {plan.period && (
                            <span className="text-text-300 text-sm">{plan.period}</span>
                          )}
                        </div>
                        <p className="mt-1 text-text-200 text-sm">{plan.description}</p>
                      </div>
                      <div className={`mt-1 ml-4 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        selectedPlan === plan.id
                          ? 'border-primary-100 bg-primary-100'
                          : 'border-bg-300'
                      }`}>
                        {selectedPlan === plan.id && (
                          <CheckIcon className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </div>

                  </button>
                ))}
              </div>

              {error && (
                <p className="text-sm text-red-400 bg-red-400 bg-opacity-10 rounded-lg px-4 py-3">
                  {error}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-text-200 hover:text-text-100 border border-bg-300 transition-all"
                >
                  <ArrowLeftIcon className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : null}
                  {loading
                    ? 'Setting up your account...'
                    : plans.find((p) => p.id === selectedPlan)?.cta || 'Continue'}
                </button>
              </div>

              <p className="text-center text-xs text-text-300">
                Cancel anytime. No hidden fees.
              </p>
            </div>
          )}

          {/* ── Step 3: Slideshow ── */}
          {step === 3 && (
            <div className="space-y-8">
              {/* Slide */}
              <div className="relative overflow-hidden rounded-2xl bg-bg-200 border border-bg-300 p-8">
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl opacity-10"
                  style={{ background: slides[slideIndex].accent }}
                />
                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{ background: `${slides[slideIndex].accent}20` }}
                  >
                    {React.createElement(slides[slideIndex].icon, {
                      className: 'w-7 h-7',
                      style: { color: slides[slideIndex].accent },
                    })}
                  </div>

                  <span
                    className="text-xs font-semibold uppercase tracking-widest"
                    style={{ color: slides[slideIndex].accent }}
                  >
                    {slides[slideIndex].tag}
                  </span>
                  <h2 className="mt-2 text-2xl font-bold text-text-100">
                    {slides[slideIndex].title}
                  </h2>
                  <p className="mt-3 text-text-200 leading-relaxed">
                    {slides[slideIndex].body}
                  </p>
                </div>
              </div>

              {/* Slide dots */}
              <div className="flex justify-center gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSlideIndex(i)}
                    className={`rounded-full transition-all duration-300 ${
                      i === slideIndex ? 'w-6 h-2 bg-primary-100' : 'w-2 h-2 bg-bg-300 hover:bg-bg-300'
                    }`}
                  />
                ))}
              </div>

              {/* Nav */}
              <div className="flex gap-3">
                {slideIndex > 0 ? (
                  <button
                    onClick={() => setSlideIndex((i) => i - 1)}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-text-200 hover:text-text-100 border border-bg-300 transition-all"
                  >
                    <ArrowLeftIcon className="w-4 h-4" /> Previous
                  </button>
                ) : (
                  <div className="flex-none w-0" />
                )}

                {slideIndex < slides.length - 1 ? (
                  <button
                    onClick={() => setSlideIndex((i) => i + 1)}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-bg-200 border border-bg-300 px-4 py-3 text-sm font-medium text-text-100 hover:bg-bg-300 transition-all"
                  >
                    Next <ArrowRightIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleFinish}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-200 transition-all disabled:opacity-60"
                  >
                    {loading ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>Enter platform <ArrowRightIcon className="w-4 h-4" /></>
                    )}
                  </button>
                )}
              </div>

              <button
                onClick={handleFinish}
                disabled={loading}
                className="w-full text-center text-sm text-text-300 hover:text-text-200 transition-colors"
              >
                Skip tour and enter platform →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
